import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useTransform } from "framer-motion";
import { getDestinations, getInstitutions, getScholarships, getNews } from "../lib/api";
import { useSEO } from "../lib/useSEO";
import { useConsultation } from "../components/Consultation";
import { MaskLines, Reveal, Dots } from "../components/motion/Reveal";
import SectionHeader from "../components/SectionHeader";
import Marquee from "../components/Marquee";
import Timeline from "../components/Timeline";
import FAQAccordion from "../components/FAQAccordion";
import CTASection from "../components/CTASection";
import Icon from "../components/Icon";
import {
  DestinationCard, InstitutionCard, ScholarshipCard, NewsCard, ServiceCard,
  TestimonialCard,
} from "../components/Cards";
import { ArrowLink } from "../components/layout/PageHero";
import {
  WHY_SUVERA, JOURNEY_STEPS, HOME_SERVICES, PATHWAY_STEPS, FAQS, TESTIMONIALS,
} from "../data/static";

const HERO_IMG_MAIN = "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&w=1000&q=80";
const HERO_IMG_SECOND = "https://images.unsplash.com/photo-1541339907198-e08756dedf3f?auto=format&fit=crop&w=700&q=80";
const TRUST_IMG = "https://images.unsplash.com/photo-1541829070764-84a7d30dd3f3?auto=format&fit=crop&w=1200&q=80";

const STATS = [
  { label: "Global Destinations", caption: "Nine countries and counting" },
  { label: "University Partners", caption: "A growing global network" },
  { label: "Student Guidance", caption: "Personalised, one-to-one" },
  { label: "End-to-End Support", caption: "From counselling to arrival" },
];

const TRUST_POINTS = [
  "Personalised counselling",
  "Destination guidance",
  "Institution selection",
  "Application support",
  "Scholarship guidance",
  "Visa assistance",
  "Pre-departure support",
];

export default function Home() {
  useSEO(
    "Suvera Academy — Your Global Education Journey Starts Here",
    "Discover the right study destination, institution and academic pathway with personalised guidance from Suvera Academy, an international education consultancy."
  );
  const [destinations, setDestinations] = useState([]);
  const [institutions, setInstitutions] = useState([]);
  const [scholarships, setScholarships] = useState([]);
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    Promise.all([getDestinations(), getInstitutions(), getScholarships(), getNews()])
      .then(([d, i, s, n]) => {
        setDestinations(d);
        setInstitutions(i);
        setScholarships(s);
        setNews(n);
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  return (
    <main data-testid="home-page">
      <Hero />
      <Marquee items={["Study in the USA", "United Kingdom", "Canada", "Australia", "New Zealand", "Germany", "Singapore", "Malaysia", "Mauritius"]} />

      {/* Chapter 01 — Trust */}
      <section className="mx-auto max-w-[1440px] px-4 py-20 sm:px-6 lg:px-10 lg:py-28" data-testid="trust-section">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <SectionHeader
              chapter="Chapter 01"
              eyebrow="Your Trusted Partner for Studying Abroad"
              title="Your Trusted Partner for Studying Abroad"
              description="Suvera Academy supports students through every stage of the international education journey — from the first conversation about your goals to the day you arrive on campus."
            />
            <Reveal>
              <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2" data-testid="trust-points-list">
                {TRUST_POINTS.map((p) => (
                  <li key={p} className="flex items-center gap-2.5 text-sm font-medium text-slate-700">
                    <Icon name="CheckCircle2" className="h-4 w-4 shrink-0 text-dot-green" />
                    {p}
                  </li>
                ))}
              </ul>
              <div className="mt-8">
                <ArrowLink to="/about" testid="trust-about-link">More About Suvera Academy</ArrowLink>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-6">
            <Reveal delay={0.15}>
              <div className="img-frame relative">
                <img src={TRUST_IMG} alt="University campus colonnade" className="max-h-[60vh] w-full object-cover lg:max-h-[520px]" loading="lazy" />
                <div className="absolute bottom-4 left-4 rounded-xl bg-white/90 px-4 py-3 backdrop-blur-md">
                  <p className="font-mono-accent text-[10px] uppercase tracking-[0.2em] text-slate-500">Suvera Academy</p>
                  <p className="font-display text-lg font-semibold text-navy">Guidance you can trust</p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
        <Reveal delay={0.1}>
          <dl className="mt-16 grid grid-cols-2 divide-slate-200 rounded-2xl border border-slate-200 bg-white lg:grid-cols-4 lg:divide-x" data-testid="stats-strip">
            {STATS.map((s) => (
              <div key={s.label} className="px-6 py-7">
                <dt className="font-mono-accent text-[10px] uppercase tracking-[0.22em] text-slate-400">{s.label}</dt>
                <dd className="mt-2 font-display text-lg font-semibold text-navy-dark">{s.caption}</dd>
              </div>
            ))}
          </dl>
        </Reveal>
      </section>

      {/* Chapter 02 — Destinations */}
      <section className="mx-auto max-w-[1440px] px-4 py-20 sm:px-6 lg:px-10 lg:py-28" data-testid="destinations-section">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <SectionHeader
            chapter="Chapter 02"
            eyebrow="Study Abroad"
            title="Study Abroad With Confidence"
            description="Explore international education opportunities across nine destinations — each with its own academic strengths, culture and career pathways."
          />
          <Reveal className="mb-12 lg:mb-16"><ArrowLink to="/destinations" testid="view-all-destinations-link">View All Destinations</ArrowLink></Reveal>
        </div>
        {loading ? (
          <CardSkeletons count={6} />
        ) : error ? (
          <ErrorState message="Destinations could not be loaded. Please refresh the page." />
        ) : (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {destinations.map((d, i) => (
              <Reveal key={d.slug} delay={(i % 3) * 0.08} className={i === 0 ? "md:col-span-2" : ""}>
                <DestinationCard d={d} large={i === 0} />
              </Reveal>
            ))}
          </div>
        )}
      </section>

      {/* Chapter 03 — Why Suvera */}
      <section className="bg-sand" data-testid="why-suvera-section">
        <div className="mx-auto max-w-[1440px] px-4 py-20 sm:px-6 lg:px-10 lg:py-28">
          <SectionHeader
            chapter="Chapter 03"
            eyebrow="Why Suvera Academy"
            title="Why Students Choose Suvera Academy"
          />
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {WHY_SUVERA.map((f, i) => (
              <Reveal key={f.title} delay={i * 0.08} className="h-full">
                <div
                  className="group flex h-full flex-col rounded-2xl border border-slate-200 bg-white p-7 transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-1.5 hover:border-navy/30 hover:shadow-[0_20px_50px_-16px_rgba(13,24,51,0.18)]"
                  data-testid={`why-card-${f.title.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-navy/5 text-navy transition-colors duration-300 group-hover:bg-navy group-hover:text-white">
                      <Icon name={f.icon} className="h-6 w-6" />
                    </span>
                    <span className="font-mono-accent text-xs text-slate-300">0{i + 1}</span>
                  </div>
                  <h3 className="mt-5 font-display text-xl font-semibold text-navy-dark">{f.title}</h3>
                  <p className="mt-2.5 flex-1 text-sm leading-relaxed text-slate-600">{f.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Chapter 04 — Journey */}
      <section className="mx-auto max-w-[1440px] px-4 py-20 sm:px-6 lg:px-10 lg:py-28" data-testid="journey-section">
        <SectionHeader
          chapter="Chapter 04"
          eyebrow="How It Works"
          title="Your Journey, Step by Step"
          description="A clear, structured process — so you always know what happens next."
        />
        <Timeline steps={JOURNEY_STEPS} />
      </section>

      {/* Chapter 05 — Institutes */}
      <section className="bg-sand" data-testid="institutes-section">
        <div className="mx-auto max-w-[1440px] px-4 py-20 sm:px-6 lg:px-10 lg:py-28">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <SectionHeader
              chapter="Chapter 05"
              eyebrow="Featured Institutes"
              title="Explore Leading Institutions"
              description="Featured partners across the UK, Mauritius, Singapore and beyond — globally recognized institutions offering world-class programs and career-driven outcomes."
            />
            <Reveal className="mb-12 lg:mb-16"><ArrowLink to="/institutes" testid="view-all-institutions-link">View All Institutions</ArrowLink></Reveal>
          </div>
          {loading ? (
            <CardSkeletons count={4} light />
          ) : error ? (
            <ErrorState message="Institutions could not be loaded. Please refresh the page." />
          ) : (
            <>
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
                {institutions.filter((i) => i.featured).slice(0, 4).map((i, idx) => (
                  <Reveal key={i.slug} delay={idx * 0.08} className="h-full"><InstitutionCard i={i} /></Reveal>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      {/* Chapter 06 — Scholarships */}
      <section className="hero-spotlight" data-testid="scholarships-section">
        <div className="mx-auto max-w-[1440px] px-4 py-20 sm:px-6 lg:px-10 lg:py-28">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <SectionHeader
              dark
              chapter="Chapter 06"
              eyebrow="Scholarships"
              title="Make Your International Education More Accessible"
              description="Scholarship opportunities across destinations and study levels — identified and matched to your profile."
            />
            <Reveal className="mb-12 lg:mb-16"><ArrowLink to="/scholarships" light testid="explore-scholarships-link">Explore Scholarships</ArrowLink></Reveal>
          </div>
          {loading ? (
            <CardSkeletons count={3} />
          ) : error ? (
            <ErrorState dark message="Scholarships could not be loaded. Please refresh the page." />
          ) : (
            <>
              <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
                {scholarships.slice(0, 3).map((s, i) => (
                  <Reveal key={s.id} delay={i * 0.08} className="h-full"><ScholarshipCard s={s} /></Reveal>
                ))}
              </div>
              <p className="mt-8 text-xs text-white/40" data-testid="scholarship-disclaimer">
                Scholarship details shown are indicative placeholders. Contact Suvera Academy for current opportunities, amounts and deadlines.
              </p>
            </>
          )}
        </div>
      </section>

      {/* Chapter 07 — Pathways */}
      <section className="mx-auto max-w-[1440px] px-4 py-20 sm:px-6 lg:px-10 lg:py-28" data-testid="pathways-section">
        <SectionHeader
          chapter="Chapter 07"
          eyebrow="Pathway Programs"
          title="Build Your Path to the Right University"
          description="Not every journey to a degree is direct. Foundation, diploma and higher diploma programs create a recognised ladder from school to university — and into your career."
        />
        <div className="flex flex-col gap-4 lg:flex-row lg:items-stretch" data-testid="pathway-flow">
          {PATHWAY_STEPS.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.08} className="flex-1">
              <div className="relative flex h-full items-center gap-4 rounded-2xl border border-slate-200 bg-white p-5 lg:flex-col lg:items-start lg:gap-0 lg:p-6">
                <span className="flex h-3 w-3 shrink-0 rounded-full lg:mb-4" style={{ backgroundColor: p.color }} />
                <div>
                  <p className="font-mono-accent text-[10px] uppercase tracking-[0.2em] text-slate-400">{p.level}</p>
                  <h3 className="mt-1 font-display text-lg font-semibold text-navy-dark">{p.title}</h3>
                  <p className="mt-1.5 hidden text-sm leading-relaxed text-slate-600 lg:block">{p.text}</p>
                </div>
                {i < PATHWAY_STEPS.length - 1 && (
                  <Icon name="ArrowRight" className="absolute -right-3 top-1/2 hidden h-5 w-5 -translate-y-1/2 text-slate-300 lg:block" />
                )}
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal className="mt-10"><ArrowLink to="/pathway-programs" testid="explore-pathways-link">Explore Pathway Programs</ArrowLink></Reveal>
      </section>

      {/* Chapter 08 — Services */}
      <section className="bg-sand" data-testid="services-section">
        <div className="mx-auto max-w-[1440px] px-4 py-20 sm:px-6 lg:px-10 lg:py-28">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <SectionHeader
              chapter="Chapter 08"
              eyebrow="Student Services"
              title="More Than Admissions"
              description="Support that begins before you apply and continues after you arrive."
            />
            <Reveal className="mb-12 lg:mb-16"><ArrowLink to="/services" testid="view-all-services-link">View All Services</ArrowLink></Reveal>
          </div>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {HOME_SERVICES.map((s, i) => <ServiceCard key={s.title} s={s} index={i} />)}
          </div>
        </div>
      </section>

      {/* Chapter 09 — Global gallery */}
      <section className="overflow-hidden bg-navy-dark py-20 lg:py-28" data-testid="global-gallery-section">
        <div className="mx-auto max-w-[1440px] px-4 sm:px-6 lg:px-10">
          <SectionHeader
            dark
            chapter="Chapter 09"
            eyebrow="Global Destinations"
            title="Where Will Your Journey Take You?"
            description="Nine destinations. One journey that is entirely yours."
          />
        </div>
        <div className="flex snap-x snap-mandatory gap-6 overflow-x-auto px-4 pb-4 sm:px-6 lg:px-10" data-testid="global-gallery-scroll">
          {loading
            ? <CardSkeletons count={4} horizontal />
            : destinations.map((d) => (
                <div key={d.slug} className="w-[300px] shrink-0 snap-start sm:w-[360px]">
                  <DestinationCard d={d} />
                </div>
              ))}
        </div>
      </section>

      {/* Chapter 10 — Testimonials */}
      <section className="mx-auto max-w-[1440px] px-4 py-20 sm:px-6 lg:px-10 lg:py-28" data-testid="testimonials-section">
        <SectionHeader
          chapter="Chapter 10"
          eyebrow="Student Experiences"
          title="Student Experiences"
        />
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {TESTIMONIALS.map((t, i) => <TestimonialCard key={i} t={t} index={i} />)}
        </div>
      </section>

      {/* Chapter 11 — News teaser */}
      {!loading && !error && news.length > 0 && (
        <section className="bg-sand" data-testid="news-section">
          <div className="mx-auto max-w-[1440px] px-4 py-20 sm:px-6 lg:px-10 lg:py-28">
            <div className="flex flex-wrap items-end justify-between gap-6">
              <SectionHeader chapter="Chapter 11" eyebrow="News & Insights" title="Latest From Suvera Academy" />
              <Reveal className="mb-12 lg:mb-16"><ArrowLink to="/news" testid="view-all-news-link">View All News</ArrowLink></Reveal>
            </div>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
              {news.slice(0, 3).map((a, i) => (
                <Reveal key={a.slug} delay={i * 0.08} className="h-full"><NewsCard a={a} /></Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Chapter 12 — FAQ */}
      <section className="mx-auto max-w-[1440px] px-4 py-20 sm:px-6 lg:px-10 lg:py-28" data-testid="faq-section">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <SectionHeader
              chapter="Chapter 12"
              eyebrow="Questions"
              title="Frequently Asked Questions"
              description="Everything students and parents usually ask before starting their journey abroad."
            />
            <Reveal>
              <div className="-mt-6 rounded-2xl bg-navy p-7" data-testid="faq-help-card">
                <p className="font-display text-lg font-semibold text-white">Still have questions?</p>
                <p className="mt-2 text-sm leading-relaxed text-white/65">Our advisors reply to every enquiry personally — usually within one working day.</p>
                <Link
                  to="/contact"
                  className="mt-5 inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-semibold text-navy transition-colors duration-200 hover:bg-dot-amber hover:text-navy-dark"
                  data-testid="faq-contact-button"
                >
                  Talk to an Advisor
                  <Icon name="ArrowRight" className="h-4 w-4" />
                </Link>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <FAQAccordion faqs={FAQS} />
          </div>
        </div>
      </section>

      <CTASection />
    </main>
  );
}

function Hero() {
  const openConsultation = useConsultation();
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const xMain = useTransform(mx, (v) => v * 20);
  const yMain = useTransform(my, (v) => v * 14);
  const xSecond = useTransform(mx, (v) => v * -30);
  const ySecond = useTransform(my, (v) => v * -20);

  const onMouseMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section
      className="hero-spotlight relative flex min-h-[100svh] items-center overflow-hidden"
      onMouseMove={onMouseMove}
      data-testid="home-hero"
    >
      <div className="relative mx-auto grid w-full max-w-[1440px] grid-cols-1 items-center gap-14 px-4 pb-20 pt-32 sm:px-6 lg:grid-cols-12 lg:px-10 lg:pb-24 lg:pt-40">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-6 flex items-center gap-3 font-mono-accent text-xs uppercase tracking-[0.28em] text-dot-amber"
          >
            <Dots size="h-1.5 w-1.5" /> International Education Consultancy
          </motion.p>
          <h1 className="font-display text-5xl font-bold leading-[1.02] tracking-tight text-white sm:text-6xl lg:text-7xl xl:text-[5.25rem]" data-testid="hero-headline">
            <MaskLines
              lines={[
                <>Your <span className="bg-gradient-to-r from-dot-blue via-dot-green to-dot-amber bg-clip-text text-transparent">Global Education</span></>,
                <>Journey Starts Here.</>,
              ]}
            />
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.55 }}
            className="mt-6 max-w-xl text-base leading-relaxed text-white/70 sm:text-lg"
          >
            Discover the right destination, institution and academic pathway with personalised guidance from Suvera Academy.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.7 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <Link
              to="/destinations"
              className="group inline-flex items-center gap-2 rounded-full bg-white px-7 py-3.5 text-sm font-semibold text-navy transition-colors duration-200 hover:bg-dot-amber hover:text-navy-dark"
              data-testid="hero-explore-destinations-button"
            >
              Explore Destinations
              <Icon name="ArrowRight" className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
            </Link>
            <button
              onClick={openConsultation}
              className="inline-flex items-center gap-2 rounded-full border border-white/25 px-7 py-3.5 text-sm font-semibold text-white transition-colors duration-200 hover:border-white/60 hover:bg-white/5"
              data-testid="hero-book-consultation-button"
            >
              Book a Free Consultation
            </button>
          </motion.div>
          <motion.dl
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            className="mt-14 grid max-w-2xl grid-cols-2 gap-3 sm:grid-cols-4"
            data-testid="hero-stats-strip"
          >
            {STATS.map((s) => (
              <div key={s.label} className="rounded-xl border border-white/10 bg-white/5 px-4 py-4 backdrop-blur-sm">
                <dt className="font-mono-accent text-[9px] uppercase tracking-[0.2em] text-white/40">{s.label}</dt>
                <dd className="mt-1.5 text-xs font-semibold leading-snug text-white/90">{s.caption}</dd>
              </div>
            ))}
          </motion.dl>
        </div>

        <div className="relative hidden lg:col-span-5 lg:block">
          <motion.div
            style={{ x: xMain, y: yMain }}
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="img-frame relative z-10 ml-auto w-[85%]"
          >
            <img src={HERO_IMG_MAIN} alt="International students studying together on campus" className="h-[420px] w-full object-cover" />
          </motion.div>
          <motion.div
            style={{ x: xSecond, y: ySecond }}
            initial={{ opacity: 0, y: 60 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="img-frame animate-float-slow absolute -bottom-10 -left-2 z-20 w-[52%]"
          >
            <img src={HERO_IMG_SECOND} alt="Graduates celebrating outside a university building" className="h-[220px] w-full object-cover" />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, delay: 0.85 }}
            className="absolute -top-6 right-0 z-20 rounded-2xl border border-white/10 bg-white/10 px-5 py-4 backdrop-blur-xl"
          >
            <p className="font-display text-3xl font-semibold text-white">9</p>
            <p className="font-mono-accent text-[10px] uppercase tracking-[0.2em] text-white/60">Destinations</p>
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 0.8 }}
        className="absolute bottom-6 left-1/2 hidden -translate-x-1/2 items-center gap-2 text-white/40 lg:flex"
        aria-hidden="true"
      >
        <span className="font-mono-accent text-[10px] uppercase tracking-[0.25em]">Scroll</span>
        <span className="h-8 w-px bg-gradient-to-b from-white/40 to-transparent" />
      </motion.div>
    </section>
  );
}

export function CardSkeletons({ count = 3, light = false, horizontal = false }) {
  return (
    <div className={horizontal ? "flex gap-6" : "grid grid-cols-1 gap-6 md:grid-cols-3"} data-testid="loading-skeleton">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className={`animate-pulse rounded-2xl ${light ? "bg-white" : "bg-slate-200/70"} ${horizontal ? "h-[300px] w-[320px] shrink-0" : "h-[280px]"}`} />
      ))}
    </div>
  );
}

export function ErrorState({ message, dark = false }) {
  return (
    <div
      className={`rounded-2xl border border-dashed p-10 text-center text-sm ${dark ? "border-white/20 text-white/60" : "border-slate-300 text-slate-500"}`}
      role="alert"
      data-testid="error-state"
    >
      {message}
    </div>
  );
}

export function EmptyState({ message, dark = false }) {
  return (
    <div
      className={`rounded-2xl border border-dashed p-10 text-center text-sm ${dark ? "border-white/20 text-white/60" : "border-slate-300 text-slate-500"}`}
      data-testid="empty-state"
    >
      {message}
    </div>
  );
}
